// Behind-the-Meter (self-consumption & peak shaving) section.
// Rendered only when the revenue model is Behind-the-Meter.
//
// Self-consumption is always on and needs no input. The three optional streams
// below are toggled individually. Enabling retail time-of-use arbitrage forces
// grid charging ON for the run; leaving it off forces grid charging OFF (the
// backend enforces both independently in read_config).
import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import MaybeTooltip from "./MaybeTooltip";
import { useLanguage } from "../context/LanguageContext";
import ManualLink from "./ManualLink";
import { assetTypeDefinition } from "../utils/projectAssets";
import {
  BTM_HELPER_TEXT,
  BTM_LEARN_MORE_TEXT,
  DEMAND_CHARGE_BASIS_OPTIONS,
  capacityChargePreset,
  DEMAND_CHARGE_INTERVAL_OPTIONS,
  DEMAND_CHARGE_PERIOD_OPTIONS,
  DEMAND_CHARGE_HELPER_TEXT,
  ESCALATION_HELPER_TEXT,
  ENERGY_SHARE_HELPER_TEXT,
  ESCALATION_NOT_USED_TEXT,
  REGULATED_ESCALATION_HELPER_TEXT,
  REGULATED_ESCALATION_LABEL,
  RETAIL_SPLIT_NO_PATH_TEXT,
  RETAIL_SPLIT_ON_TEXT,
  retailSplitState,
  SURPLUS_HELPER_TEXT,
  SURPLUS_PRICE_HELPER_TEXT,
  UNPAID_SURPLUS_EXPORT_NOTE,
  isUnpaidSurplusExport,
  SURPLUS_TREATMENT_OPTIONS,
  TOU_ARBITRAGE_HELPER_TEXT,
  WEAR_COST_MODE_OPTIONS,
  WEAR_COST_MOVED_TEXT,
  TOU_GR_INJECTING_LOCKED_TEXT,
  touArbitrageBlockedByFramework,
  NET_BILLING_HELPER_TEXT,
  NET_BILLING_INJECTION_BASIS_OPTIONS,
  NET_BILLING_SETTLEMENT_PERIOD_OPTIONS,
  NET_BILLING_SURPLUS_CREDIT_OPTIONS,
  NET_BILLING_INJECTION_RECIPIENT_OPTIONS,
  netBillingCountryDefaults,
  GRID_INJECTION_OPTIONS,
  GRID_INJECTION_HELPER_TEXT,
  GRID_INJECTION_HELPER_TEXT_STANDALONE,
  ZERO_FEED_IN_SURPLUS_LOCKED_TEXT,
  ZERO_FEED_IN_NET_BILLING_LOCKED_TEXT,
  ZERO_FEED_IN_TOU_NOTE,
  zeroFeedInCountryDefaults,
  isZeroFeedIn,
  isPvOnlyExport,
  pvOnlyExportCountryDefaults,
  PV_ONLY_EXPORT_NOTE,
  PV_ONLY_EXPORT_TOU_NOTE,
  btmWarnings,
  validateBtm,
} from "./config/btm";
import {
  ESCALATION_NOT_USED_DYNAMIC_TEXT,
  RETAIL_DYNAMIC_ON_TEXT,
  TOU_DYNAMIC_HELPER_TEXT,
} from "./config/dynamicTariff";
import {
  VNB_CHARGE_HELPER,
  VNB_CHARGE_LABEL,
  VNB_GRID_CHARGING_HINT,
  VNB_LOCKED_STREAMS_NOTE,
  VNB_MODE,
  VNB_SECTION_TEXT,
  VNB_SECTION_TITLE,
  VNB_SELF_CONSUMPTION_TEXT,
  VNB_SURPLUS_HELPER,
  VNB_SURPLUS_LABEL,
  defaultVirtualNetBilling,
  isVirtualNetBilling,
} from "./config/virtualNetBilling";

const DC_BTM_NOTE =
  "DC-coupled hybrid: the battery charges from the PV DC bus only. Grid charging and retail time-of-use arbitrage are not supported in this study.";
const DC_TOU_OFF_HINT =
  "Retail time-of-use arbitrage is unavailable with DC coupling because it requires grid charging.";
const DC_TOU_ON_HINT =
  "Turn off retail time-of-use arbitrage before running the DC-coupled study; it would charge the battery from the grid.";
const DC_PV_ONLY_EXPORT_DESCRIPTION =
  "The connection may export PV surplus, settled by the surplus treatment and net billing settings below. PV and battery share one inverter; the battery serves the site load only, never exports and charges only from the PV DC bus.";
const DC_ZERO_FEED_IN_DESCRIPTION =
  "No energy is exported at the connection point. PV and battery share one inverter; surplus PV may charge the battery before it is curtailed. The battery does not charge from the grid in this DC-coupled study.";
const DC_PV_ONLY_CONNECTION_NOTE =
  "The separate-inverter PV-only export framework is not claimed for this shared DC inverter. Verify the connection and metering terms for the chosen equipment before relying on the study.";

export default function BtmSection({ value, onChange, cfg, isStandaloneBess }) {
  const { language } = useLanguage();
  const isGreek = language === "el";
  const btm = value;
  const validation = validateBtm(btm, cfg);
  // Retail price in two parts: needs an energy share AND a forward price path.
  const retailSplit = retailSplitState(btm, cfg);
  const warnings = btmWarnings(btm, cfg);

  // Don't flag a field red the moment a toggle reveals it — the user has not
  // had a chance to type yet. Show the error once they have touched it (or
  // already entered something invalid); the run is blocked either way by
  // validateBtm in FeasibilitySimulation, so nothing slips through.
  const [touched, setTouched] = useState({});
  const markTouched = (field) => setTouched((prev) => ({ ...prev, [field]: true }));
  const showError = (field, currentValue) =>
    Boolean(validation[field]) &&
    (Boolean(touched[field]) || (currentValue !== "" && currentValue !== undefined && currentValue !== null));

  const demandCharge = btm.demandCharge || {};
  const surplus = btm.surplus || {};
  const touArbitrage = btm.touArbitrage || {};
  const wearCost = btm.wearCost || {};
  const retailTariff = btm.retailTariff || {};
  const netBilling = btm.netBilling || {};
  const gridInjection = btm.gridInjection || {};
  const hasBess = assetTypeDefinition(cfg?.systemType)?.value !== "Standalone PV";
  const isDcCoupled = hasBess && cfg?.bess?.topology === "dc-coupled";

  const patch = (partial) => onChange({ ...btm, ...partial });
  const patchGridInjection = (partial) =>
    patch({ gridInjection: { ...gridInjection, ...partial } });
  const patchDemandCharge = (partial) =>
    patch({ demandCharge: { ...demandCharge, ...partial } });
  const patchSurplus = (partial) => patch({ surplus: { ...surplus, ...partial } });
  const patchNetBilling = (partial) =>
    patch({ netBilling: { ...netBilling, ...partial } });

  const numberOrBlank = (raw) => (raw === "" ? "" : Number(raw));

  const infoIcon = (title) => (
    <InputAdornment position="end">
      <MaybeTooltip title={title} placement="top" arrow>
        <IconButton size="small" tabIndex={-1}>
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>
      </MaybeTooltip>
    </InputAdornment>
  );

  const basisDescription =
    DEMAND_CHARGE_BASIS_OPTIONS.find((o) => o.value === demandCharge.basis)?.description || "";
  // Greece: a battery that may inject may not charge from the grid (art. 8).
  const touBlocked = touArbitrageBlockedByFramework(btm, cfg);
  const wearMode = WEAR_COST_MODE_OPTIONS.some((o) => o.value === wearCost.mode)
    ? wearCost.mode
    : "auto";
  const isAnnualPeriod = demandCharge.measurementPeriod === "annual";
  // Offered, never applied on its own. The zone tells us the country, not the
  // customer's voltage level or what their supplier actually wrote.
  const zonePreset = capacityChargePreset(cfg?.biddingZone);
  const presetMatches =
    zonePreset &&
    (demandCharge.measurementInterval || "hourly") === zonePreset.measurementInterval &&
    (demandCharge.measurementPeriod || "monthly") === zonePreset.measurementPeriod &&
    (demandCharge.basis || "monthly_peak") === zonePreset.basis;
  const applyZonePreset = () =>
    patchDemandCharge({
      measurementInterval: zonePreset.measurementInterval,
      measurementPeriod: zonePreset.measurementPeriod,
      basis: zonePreset.basis,
    });
  const intervalDescription =
    DEMAND_CHARGE_INTERVAL_OPTIONS.find(
      (o) => o.value === (demandCharge.measurementInterval || "hourly")
    )?.description || "";
  const periodDescription =
    DEMAND_CHARGE_PERIOD_OPTIONS.find(
      (o) => o.value === (demandCharge.measurementPeriod || "monthly")
    )?.description || "";
  const treatmentDescription =
    SURPLUS_TREATMENT_OPTIONS.find((o) => o.value === surplus.treatment)?.description || "";

  // Grid injection regime. Zero feed-in locks the surplus and net-billing
  // sections: the station never exports, so there is nothing to sell or net.
  const zeroFeedIn = isZeroFeedIn(btm);
  // "Export from PV only" keeps both sections in force (the PV surplus IS
  // exported and settled) and confines the battery to the site load in the
  // dispatch. It needs PV, so a standalone battery does not get the option.
  const pvOnlySelected = isPvOnlyExport(btm);
  const pvOnlyExport = !isStandaloneBess && pvOnlySelected;
  // Virtual net billing (Greece): the station injects everything and is
  // netted against the supplies; it settles the surplus itself and switches
  // peak shaving and ToU off (config/virtualNetBilling.js).
  const virtualNb = isVirtualNetBilling(btm);
  const vnb = btm.virtualNetBilling || {};
  const injectionMode = zeroFeedIn ? "zero_feed_in" : pvOnlySelected ? "pv_only" : virtualNb ? VNB_MODE : "allowed";
  const injectionDescription = virtualNb
    ? GRID_INJECTION_OPTIONS.find((o) => o.value === VNB_MODE)?.description || ""
    : !hasBess
    ? (isGreek
        ? injectionMode === "zero_feed_in" ? "Δεν γίνεται έγχυση. Το Φ/Β καλύπτει το φορτίο και η περίσσεια περικόπτεται."
          : "Η περίσσεια Φ/Β εξάγεται μόνο εφόσον επιτρέπεται και αποτιμάται με τις παρακάτω ρυθμίσεις."
        : injectionMode === "zero_feed_in" ? "No injection: PV supplies the load and surplus is curtailed."
          : "PV surplus is exported only where permitted and valued under the settings below.")
    : isDcCoupled && injectionMode === "pv_only"
    ? DC_PV_ONLY_EXPORT_DESCRIPTION
    : isDcCoupled && injectionMode === "zero_feed_in"
      ? DC_ZERO_FEED_IN_DESCRIPTION
      : GRID_INJECTION_OPTIONS.find((o) => o.value === injectionMode)?.description || "";
  // For a standalone battery the PV-only option is offered only while it is
  // the stored value (e.g. the system type was switched afterwards), and then
  // disabled, so the select never shows a value it has no option for and the
  // validation message below says what to pick instead.
  const isGreece = String(gridInjection.country || cfg?.biddingZone || "").trim().toUpperCase().startsWith("GR");
  const injectionOptions = GRID_INJECTION_OPTIONS.filter(
    (option) => (!(option.hybridOnly && isStandaloneBess) && !(option.greeceOnly && !isGreece))
      || option.value === injectionMode
  ).map((option) => ({
    ...option,
    disabled: Boolean(option.hybridOnly && isStandaloneBess),
  }));
  const zfiDefaults = zeroFeedInCountryDefaults(gridInjection.country || cfg?.biddingZone || "");
  const pvoDefaults = pvOnlyExportCountryDefaults(gridInjection.country || cfg?.biddingZone || "");

  // Net billing: country follows the project bidding zone unless overridden;
  // blank enums resolve to the country default, exactly as the backend does.
  const nbCountry = netBilling.country || cfg?.biddingZone || "";
  const nbDefaults = netBillingCountryDefaults(nbCountry);
  const nbBasis = netBilling.injectionPriceBasis || nbDefaults.injectionPriceBasis;
  const nbPeriod = netBilling.settlementPeriod || nbDefaults.settlementPeriod;
  const nbCredit = netBilling.surplusCreditTreatment || nbDefaults.surplusCreditTreatment;
  const nbBasisDescription =
    NET_BILLING_INJECTION_BASIS_OPTIONS.find((o) => o.value === nbBasis)?.description || "";
  const nbPeriodDescription =
    NET_BILLING_SETTLEMENT_PERIOD_OPTIONS.find((o) => o.value === nbPeriod)?.description || "";
  const nbCreditDescription =
    NET_BILLING_SURPLUS_CREDIT_OPTIONS.find((o) => o.value === nbCredit)?.description || "";
  const nbRecipient = netBilling.injectionRevenueRecipient || "host";
  const nbRecipientDescription =
    NET_BILLING_INJECTION_RECIPIENT_OPTIONS.find((o) => o.value === nbRecipient)?.description ||
    "";

  const changeInjectionMode = (mode) => {
    // The supplies block is created on first choice and kept afterwards, so
    // switching away and back restores it; it is sent only under this regime.
    if (mode === VNB_MODE && !btm.virtualNetBilling) {
      patch({ gridInjection: { ...gridInjection, mode }, virtualNetBilling: defaultVirtualNetBilling() });
    } else {
      patchGridInjection({ mode });
    }
  };
  const patchVnb = (partial) => patch({ virtualNetBilling: { ...vnb, ...partial } });
  // The supplies themselves are entered on the Technical page, with the
  // consumption (VirtualNetBillingSupplies); their problems are listed here too.
  const vnbErrors = ["vnbSystem", "vnbTransformer", "vnbCountry", "vnbTariff", "vnbSupplies", "vnbShares", "vnbFiles", "vnbSum", "vnbSupplyPrice"]
    .map((key) => validation[key])
    .filter(Boolean);

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "success.main",
        backgroundColor: "background.default",
        mt: 2,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
        Behind-the-Meter — Self-Consumption &amp; Peak Shaving
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {BTM_HELPER_TEXT}
      </Typography>
      {isDcCoupled && <Alert severity="info" sx={{ mt: 1.5 }}>{DC_BTM_NOTE}</Alert>}

      {[validation.demand, validation.schedule, validation.tariffs, validation.peakWindow]
        .filter(Boolean)
        .map((message, idx) => (
          <Alert severity="warning" sx={{ mt: 1.5 }} key={idx}>
            {message}
          </Alert>
        ))}
      {validation.streams && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          {validation.streams}
        </Alert>
      )}

      <Accordion
        elevation={0}
        disableGutters
        sx={{ backgroundColor: "transparent", "&:before": { display: "none" }, mt: 1 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Learn more about behind-the-meter savings
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {BTM_LEARN_MORE_TEXT}
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* -------------------- 1. Self-consumption (always on) -------------------- */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Self-consumption offset — always included
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {virtualNb
            ? VNB_SELF_CONSUMPTION_TEXT
            : isStandaloneBess
            ? "With no generation on site, the battery serves the load from stored energy. Enable peak shaving and/or time-of-use arbitrage below so it has something to earn."
            : "Generation and stored energy serve the site load, so that energy is not bought from the supplier. Valued at the site's retail price for each hour, using the monthly tariffs from the Feasibility > Technical page."}
        </Typography>
      </Box>

      {/* -------------------- 2. Peak shaving -------------------- */}
      <Box id="btm-capacity-charge" sx={{ mt: 1.5, scrollMarginTop: 80 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!virtualNb && Boolean(demandCharge.enabled)}
              onChange={(e) => patchDemandCharge({ enabled: e.target.checked })}
              disabled={virtualNb}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Peak shaving — reduce the capacity (demand) charge
            </Typography>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          {virtualNb ? VNB_LOCKED_STREAMS_NOTE : DEMAND_CHARGE_HELPER_TEXT}
        </Typography>

        {demandCharge.enabled && !virtualNb && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 2fr" },
              gap: 2,
              mt: 1.5,
              ml: 4,
            }}
          >
            {zonePreset && (
              <Alert
                // The parent is a 1fr/2fr grid, so without this the notice is
                // squeezed into the narrow first column and reads as a sliver
                // beside the rate field instead of a banner above the section.
                sx={{ gridColumn: "1 / -1" }}
                severity={presetMatches ? "success" : "info"}
                action={
                  presetMatches ? null : (
                    <Button color="inherit" size="small" onClick={applyZonePreset}>
                      Apply
                    </Button>
                  )
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {zonePreset.label}
                  {isGreek ? (presetMatches ? " — οι ρυθμίσεις ταιριάζουν" : " — οι ρυθμίσεις διαφέρουν") : (presetMatches ? " — settings match" : " — settings differ")}
                </Typography>
                <Typography variant="caption" sx={{ display: "block" }}>
                  {zonePreset.note}
                </Typography>
              </Alert>
            )}
            {/* The rate follows the period: a EUR/kW-year figure applied
                monthly, or the reverse, silently changes the bill. Only the
                field that applies is shown. */}
            <FormControl fullWidth>
              <InputLabel>Billing period</InputLabel>
              <Select
                label="Billing period"
                value={demandCharge.measurementPeriod || "monthly"}
                onChange={(e) =>
                  patchDemandCharge({ measurementPeriod: e.target.value })
                }
              >
                {DEMAND_CHARGE_PERIOD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{periodDescription}</FormHelperText>
            </FormControl>
            {isAnnualPeriod ? (
              <TextField
                fullWidth
                label="Capacity charge (EUR/kW-year)"
                type="number"
                value={demandCharge.eurPerKwYear ?? ""}
                onChange={(e) => {
                  markTouched("demandChargeRate");
                  patchDemandCharge({ eurPerKwYear: numberOrBlank(e.target.value) });
                }}
                onBlur={() => markTouched("demandChargeRate")}
                error={showError("demandChargeRate", demandCharge.eurPerKwYear)}
                helperText={
                  showError("demandChargeRate", demandCharge.eurPerKwYear)
                    ? validation.demandChargeRate
                    : "Billed once on the year's highest demand. A German Leistungspreis is typically 100-300 EUR/kW-year."
                }
                inputProps={{ min: 0, step: "any" }}
                InputProps={{
                  endAdornment: infoIcon(
                    "Germany's Leistungspreis is charged per kW of the highest 15-minute average demand of the calendar year, quoted per kW per year. Do not convert it to a monthly figure - the two are not equivalent."
                  ),
                }}
              />
            ) : (
              <TextField
                fullWidth
                label="Capacity charge (EUR/kW-month)"
                type="number"
                value={demandCharge.eurPerKwMonth ?? ""}
                onChange={(e) => {
                  markTouched("demandChargeRate");
                  patchDemandCharge({ eurPerKwMonth: numberOrBlank(e.target.value) });
                }}
                onBlur={() => markTouched("demandChargeRate")}
                error={showError("demandChargeRate", demandCharge.eurPerKwMonth)}
                helperText={
                  showError("demandChargeRate", demandCharge.eurPerKwMonth)
                    ? validation.demandChargeRate
                    : "Billed on each month's highest demand. Take the all-in capacity component from the site's bill - the Greek MV transmission element alone is only about 1.4 EUR/kW-month."
                }
                inputProps={{ min: 0, step: "any" }}
                InputProps={{
                  endAdornment: infoIcon(
                    "Commercial and industrial supply contracts bill a charge per kW of the highest metered demand in the period, separately from the energy charge. Find it on the site's bill, often labelled capacity, demand or agreed power."
                  ),
                }}
              />
            )}
            <Typography variant="caption" color="text.secondary" sx={{ gridColumn: "1 / -1" }}>
              {isGreek
                ? "Για σύγκριση καθαρού οικονομικού κόστους, καταχωρίστε εδώ τη χρέωση ισχύος μαζί με τυχόν μη ανακτήσιμο ΦΠΑ. Μην προσθέσετε ξανά τη χρέωση στα €/kWh των τιμολογίων."
                : "For the net economic-cost comparison, include any non-recoverable VAT in this capacity rate. Do not also add it to the tariff's per-kWh charges."}
            </Typography>
            <FormControl fullWidth>
              <InputLabel>Averaging interval</InputLabel>
              <Select
                label="Averaging interval"
                value={demandCharge.measurementInterval || "hourly"}
                onChange={(e) =>
                  patchDemandCharge({ measurementInterval: e.target.value })
                }
              >
                {DEMAND_CHARGE_INTERVAL_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{intervalDescription}</FormHelperText>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Measured against</InputLabel>
              <Select
                label="Measured against"
                value={demandCharge.basis}
                onChange={(e) => patchDemandCharge({ basis: e.target.value })}
              >
                {DEMAND_CHARGE_BASIS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>{basisDescription}</FormHelperText>
            </FormControl>
          </Box>
        )}
      </Box>

      {/* -------------------- 3. Retail ToU arbitrage -------------------- */}
      {hasBess && <>
      <Box sx={{ mt: 1.5 }}>
        <FormControlLabel
          control={
            <Checkbox
              // Always on for a standalone battery: it has no other energy
              // source, so it always charges from the grid. Shown checked
              // whatever is stored; the payload and the backend force it too.
              checked={!virtualNb && (isStandaloneBess || (isDcCoupled
                ? Boolean(touArbitrage.enabled)
                : (!touBlocked && Boolean(touArbitrage.enabled))))}
              onChange={(e) => patch({ touArbitrage: { ...touArbitrage, enabled: e.target.checked } })}
              disabled={virtualNb || isStandaloneBess || (isDcCoupled ? !touArbitrage.enabled : touBlocked)}
            />
          }
          label={
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Retail time-of-use arbitrage
              {isStandaloneBess ? " — always on for a standalone BESS" : ""}
            </Typography>
          }
        />
        <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
          {virtualNb
            ? VNB_GRID_CHARGING_HINT
            : isStandaloneBess
            ? "A standalone battery has no other energy source, so it is always charged from the grid. Grid charging stays ON for this run."
            : isDcCoupled
              ? (touArbitrage.enabled ? DC_TOU_ON_HINT : DC_TOU_OFF_HINT)
            : touBlocked
              ? TOU_GR_INJECTING_LOCKED_TEXT
              : retailSplit.dynamic
                ? TOU_DYNAMIC_HELPER_TEXT
                : TOU_ARBITRAGE_HELPER_TEXT}
        </Typography>
        {touArbitrage.enabled && !virtualNb && !isStandaloneBess && !touBlocked && !isDcCoupled && (
          <Alert severity="info" sx={{ mt: 1, ml: 4 }}>
            'Enable Grid Charging' on the BESS page will be switched ON for this
            run. The cost of the imported energy is charged in full against the
            saving, so the reported benefit is the net spread.
          </Alert>
        )}
        {zeroFeedIn && !isDcCoupled && zfiDefaults.gridChargingPermitted && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4, mt: 0.5 }}>
            {ZERO_FEED_IN_TOU_NOTE}
          </Typography>
        )}
        {pvOnlyExport && !isDcCoupled && touArbitrage.enabled && pvoDefaults.gridChargingPermitted && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4, mt: 0.5 }}>
            {PV_ONLY_EXPORT_TOU_NOTE}
          </Typography>
        )}
      </Box>

      {/* -------------------- 3a. Battery wear priced in the dispatch -------------------- */}
      {/* Set on the BESS page (Battery Performance), beside the cycle life and
          state-of-charge window it is derived from. Only the current choice and
          any error stay here, so the run is never blocked by a field the user
          cannot see. */}
      <Box sx={{ mt: 1.5 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Battery wear in the dispatch
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {WEAR_COST_MOVED_TEXT}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          {WEAR_COST_MODE_OPTIONS.find((o) => o.value === wearMode)?.label || ""}
        </Typography>
        {validation.wearCost && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {validation.wearCost}
          </Alert>
        )}
      </Box>

      {/* -------------------- 3b. Grid injection regime -------------------- */}
      </>}
      {/* Shown for a standalone battery too: a zero-injection standalone
          storage station is its own case in the Greek framework (art. 8α). */}
      {(
        <Box sx={{ mt: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Grid injection regime
            </Typography>
            {/* Under virtual net billing the guide opens its own chapter. */}
            <ManualLink settlementType={cfg?.settlementType} section={virtualNb ? "vnb" : "regime"} />
          </Box>
          <Typography variant="body2" color="text.secondary">
            {isStandaloneBess ? GRID_INJECTION_HELPER_TEXT_STANDALONE : GRID_INJECTION_HELPER_TEXT}
          </Typography>
          <FormControl fullWidth sx={{ mt: 1.5 }} error={Boolean(validation.gridInjection)} id="field-btm-injection">
            <InputLabel>Grid injection</InputLabel>
            <Select
              label="Grid injection"
              value={injectionMode}
              onChange={(e) => changeInjectionMode(e.target.value)}
            >
              {injectionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{validation.gridInjection || injectionDescription}</FormHelperText>
          </FormControl>
          {zeroFeedIn && (
            <Alert severity={zfiDefaults.documented ? "success" : "warning"} sx={{ mt: 1.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {zfiDefaults.countryLabel
                  ? isGreek
                    ? `Χώρα: ${zfiDefaults.countryLabel === "Greece" ? "Ελλάδα" : zfiDefaults.countryLabel} — ${zfiDefaults.documented ? "τεκμηριωμένο πλαίσιο" : "παραδοχή χρήστη, απαιτείται έλεγχος"}`
                    : `Country: ${zfiDefaults.countryLabel}${zfiDefaults.documented ? " — documented framework" : " — user-supplied assumption, verify"}`
                  : isGreek
                    ? "Δεν έχει οριστεί χώρα — ορίστε τη ζώνη αγοράς του έργου στη σελίδα Γενικά"
                    : "Country not set — set the project bidding zone on the General page"}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {zfiDefaults.note}
              </Typography>
              {zfiDefaults.legalBasis && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                  {zfiDefaults.legalBasis}
                </Typography>
              )}
            </Alert>
          )}
          {pvOnlyExport && hasBess && (
            <Alert severity={isDcCoupled ? "warning" : pvoDefaults.documented ? "success" : "warning"} sx={{ mt: 1.5 }}>
              {isDcCoupled ? (
                <Typography variant="body2">{DC_PV_ONLY_CONNECTION_NOTE}</Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {pvoDefaults.countryLabel
                      ? isGreek
                        ? `Χώρα: ${pvoDefaults.countryLabel === "Greece" ? "Ελλάδα" : pvoDefaults.countryLabel} — ${pvoDefaults.documented ? "τεκμηριωμένο πλαίσιο" : "παραδοχή χρήστη, απαιτείται έλεγχος"}`
                        : `Country: ${pvoDefaults.countryLabel}${pvoDefaults.documented ? " — documented framework" : " — user-supplied assumption, verify"}`
                      : isGreek
                        ? "Δεν έχει οριστεί χώρα — ορίστε τη ζώνη αγοράς του έργου στη σελίδα Γενικά"
                        : "Country not set — set the project bidding zone on the General page"}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {PV_ONLY_EXPORT_NOTE}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {pvoDefaults.note}
                  </Typography>
                  {pvoDefaults.legalBasis && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      {pvoDefaults.legalBasis}
                    </Typography>
                  )}
                </>
              )}
            </Alert>
          )}
          {virtualNb && (
            <Box id="btm-virtual-net-billing" sx={{ mt: 1.5, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {VNB_SECTION_TITLE}
                </Typography>
                <ManualLink settlementType={cfg?.settlementType} section="vnbSettlement" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {VNB_SECTION_TEXT}
              </Typography>
              {vnbErrors.map((message) => (
                <Alert severity="error" sx={{ mb: 1.5 }} key={message}>
                  {message}
                </Alert>
              ))}
              <TextField
                fullWidth
                label={VNB_CHARGE_LABEL}
                type="number"
                value={vnb.nettedChargeEurPerMwh ?? ""}
                onChange={(e) => {
                  markTouched("vnbCharge");
                  patchVnb({ nettedChargeEurPerMwh: numberOrBlank(e.target.value) });
                }}
                onBlur={() => markTouched("vnbCharge")}
                error={showError("vnbCharge", vnb.nettedChargeEurPerMwh)}
                helperText={showError("vnbCharge", vnb.nettedChargeEurPerMwh) ? validation.vnbCharge : VNB_CHARGE_HELPER}
                inputProps={{ min: 0, step: "any" }}
              />
              <FormControlLabel
                sx={{ mt: 1 }}
                control={
                  <Checkbox
                    checked={vnb.surplusCompensated !== false}
                    onChange={(e) => patchVnb({ surplusCompensated: e.target.checked })}
                  />
                }
                label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{VNB_SURPLUS_LABEL}</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", ml: 4 }}>
                {VNB_SURPLUS_HELPER}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* -------------------- 4. Surplus export -------------------- */}
      {/* Under virtual net billing the surplus is settled by the scheme above. */}
      {!isStandaloneBess && !virtualNb && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Surplus export
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {SURPLUS_HELPER_TEXT}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: !zeroFeedIn && !netBilling.enabled && surplus.treatment === "fixed_price" ? "2fr 1fr" : "1fr",
              },
              gap: 2,
              mt: 1.5,
            }}
          >
            <FormControl fullWidth disabled={zeroFeedIn || Boolean(netBilling.enabled)}>
              <InputLabel>Treatment of surplus generation</InputLabel>
              <Select
                label="Treatment of surplus generation"
                value={zeroFeedIn ? "none" : surplus.treatment}
                onChange={(e) => patchSurplus({ treatment: e.target.value })}
              >
                {SURPLUS_TREATMENT_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {zeroFeedIn
                  ? ZERO_FEED_IN_SURPLUS_LOCKED_TEXT
                  : netBilling.enabled
                    ? "Superseded by net billing below — the injected surplus is valued under the net-billing scheme, not at a fixed feed-in price."
                    : treatmentDescription}
              </FormHelperText>
            </FormControl>
            {/* The price is not used while net billing values the surplus, so
                it is not offered for editing then (it is kept, and comes back
                when net billing is switched off). */}
            {!zeroFeedIn && !netBilling.enabled && surplus.treatment === "fixed_price" && (
              <TextField
                fullWidth
                label="Feed-in price (EUR/MWh)"
                type="number"
                value={surplus.feedInEurPerMwh ?? ""}
                onChange={(e) => {
                  markTouched("feedInPrice");
                  patchSurplus({ feedInEurPerMwh: numberOrBlank(e.target.value) });
                }}
                onBlur={() => markTouched("feedInPrice")}
                error={showError("feedInPrice", surplus.feedInEurPerMwh)}
                helperText={
                  showError("feedInPrice", surplus.feedInEurPerMwh)
                    ? validation.feedInPrice
                    : SURPLUS_PRICE_HELPER_TEXT
                }
                inputProps={{ min: 0, step: "any" }}
                InputProps={{
                  endAdornment: infoIcon(
                    "A single fixed price per MWh under a feed-in or surplus-purchase agreement. Export is still limited by the grid connection capacity set on this page. Net billing with carried-over energy credits is a different arrangement and is not modelled."
                  ),
                }}
              />
            )}
          </Box>
          {isUnpaidSurplusExport(btm) && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {UNPAID_SURPLUS_EXPORT_NOTE}
            </Alert>
          )}
        </Box>
      )}

      {/* -------------------- 4b. Net billing (καθαρή χρέωση) -------------------- */}
      {!isStandaloneBess && !virtualNb && (
        <Box sx={{ mt: 1.5 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={!zeroFeedIn && Boolean(netBilling.enabled)}
                onChange={(e) => patchNetBilling({ enabled: e.target.checked })}
                disabled={zeroFeedIn}
              />
            }
            label={
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Net Billing — value the injected surplus
              </Typography>
            }
          />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
            {zeroFeedIn ? ZERO_FEED_IN_NET_BILLING_LOCKED_TEXT : NET_BILLING_HELPER_TEXT}
          </Typography>

          {!zeroFeedIn && netBilling.enabled && (
            <Box sx={{ ml: 4, mt: 1.5 }}>
              <Alert severity={nbDefaults.documented ? "success" : "warning"} sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {nbDefaults.countryLabel
                    ? isGreek
                      ? `Χώρα: ${nbDefaults.countryLabel === "Greece" ? "Ελλάδα" : nbDefaults.countryLabel} — ${nbDefaults.documented ? "τεκμηριωμένο πλαίσιο" : "παραδοχή χρήστη, απαιτείται έλεγχος"}`
                      : `Country: ${nbDefaults.countryLabel}${nbDefaults.documented ? " — documented framework" : " — user-supplied assumption, verify"}`
                    : isGreek
                      ? "Δεν έχει οριστεί χώρα — ορίστε τη ζώνη αγοράς του έργου στη σελίδα Γενικά"
                      : "Country not set — set the project bidding zone on the General page"}
                </Typography>
                <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                  {nbDefaults.note}{isGreek ? " Η συμμετοχή στην αγορά γίνεται μέσω " : " Represented in the market by the "}{nbDefaults.counterpartyRole}.
                </Typography>
              </Alert>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: nbBasis === "fixed_agreed" ? "2fr 1fr" : "1fr",
                  },
                  gap: 2,
                }}
              >
                <FormControl fullWidth>
                  <InputLabel>Injected surplus valued at</InputLabel>
                  <Select
                    label="Injected surplus valued at"
                    value={nbBasis}
                    onChange={(e) => patchNetBilling({ injectionPriceBasis: e.target.value })}
                  >
                    {NET_BILLING_INJECTION_BASIS_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{nbBasisDescription}</FormHelperText>
                </FormControl>
                {nbBasis === "fixed_agreed" && (
                  <TextField
                    fullWidth
                    label="Fixed injection price (EUR/MWh)"
                    type="number"
                    value={netBilling.fixedInjectionPriceEurPerMwh ?? ""}
                    onChange={(e) => {
                      markTouched("netBillingFixedPrice");
                      patchNetBilling({
                        fixedInjectionPriceEurPerMwh: numberOrBlank(e.target.value),
                      });
                    }}
                    onBlur={() => markTouched("netBillingFixedPrice")}
                    error={showError("netBillingFixedPrice", netBilling.fixedInjectionPriceEurPerMwh)}
                    helperText={
                      showError("netBillingFixedPrice", netBilling.fixedInjectionPriceEurPerMwh)
                        ? validation.netBillingFixedPrice
                        : "The fixed price the aggregator pays for injected surplus. The third-party investment model quotes around 70-80 EUR/MWh."
                    }
                    inputProps={{ min: 0, step: "any" }}
                    InputProps={{
                      endAdornment: infoIcon(
                        "A single fixed price per MWh agreed with the aggregator for the whole horizon. Choose the day-ahead market basis instead if the injected surplus is settled at the wholesale price."
                      ),
                    }}
                  />
                )}
                <FormControl fullWidth>
                  <InputLabel>Settlement period</InputLabel>
                  <Select
                    label="Settlement period"
                    value={nbPeriod}
                    onChange={(e) => patchNetBilling({ settlementPeriod: e.target.value })}
                  >
                    {NET_BILLING_SETTLEMENT_PERIOD_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{nbPeriodDescription}</FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Surplus credit above consumption</InputLabel>
                  <Select
                    label="Surplus credit above consumption"
                    value={nbCredit}
                    onChange={(e) => patchNetBilling({ surplusCreditTreatment: e.target.value })}
                  >
                    {NET_BILLING_SURPLUS_CREDIT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{nbCreditDescription}</FormHelperText>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Injected-surplus revenue recipient</InputLabel>
                  <Select
                    label="Injected-surplus revenue recipient"
                    value={nbRecipient}
                    onChange={(e) =>
                      patchNetBilling({ injectionRevenueRecipient: e.target.value })
                    }
                  >
                    {NET_BILLING_INJECTION_RECIPIENT_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>{nbRecipientDescription}</FormHelperText>
                </FormControl>
                {nbRecipient === "third_party" && (
                  <TextField
                    fullWidth
                    label="Investor recovery basis (EUR/MWh, optional)"
                    type="number"
                    value={netBilling.thirdPartyInvestorPriceEurPerMwh ?? ""}
                    onChange={(e) =>
                      patchNetBilling({
                        thirdPartyInvestorPriceEurPerMwh: numberOrBlank(e.target.value),
                      })
                    }
                    helperText="Optional: the third party's own recovery rate (e.g. 70-80 EUR/MWh). Informational only — it does not change the modelled totals."
                    inputProps={{ step: "any" }}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* -------------------- 5. Retail escalation -------------------- */}
      <Box sx={{ mt: 2 }} id="field-btm-escalation">
        <TextField
          fullWidth
          label="Retail price escalation (%/year)"
          type="number"
          disabled={retailSplit.active}
          value={retailTariff.escalationPctYear ?? ""}
          onChange={(e) =>
            patch({
              retailTariff: {
                ...retailTariff,
                escalationPctYear: numberOrBlank(e.target.value),
              },
            })
          }
          onBlur={() => markTouched("escalation")}
          error={showError("escalation", retailTariff.escalationPctYear)}
          helperText={
            showError("escalation", retailTariff.escalationPctYear)
              ? validation.escalation
              : retailSplit.active
                ? retailSplit.dynamic
                  ? ESCALATION_NOT_USED_DYNAMIC_TEXT
                  : ESCALATION_NOT_USED_TEXT
                : ESCALATION_HELPER_TEXT
          }
          inputProps={{ step: "any" }}
          InputProps={{
            endAdornment: infoIcon(
              "Applied to the savings projection over the assessment period. Leave at 0 for a flat, deliberately conservative projection."
            ),
          }}
        />
        {/* The retail price in two parts: energy on the forward price path,
            the regulated rest on inflation. Blank = one escalation. A dynamic
            tariff splits the price by itself, so no share is asked for. */}
        {!retailSplit.dynamic && (
        <TextField
          fullWidth
          sx={{ mt: 2 }}
          label="Energy share of the retail price (%)"
          type="number"
          value={retailTariff.energySharePct ?? ""}
          onChange={(e) =>
            patch({
              retailTariff: {
                ...retailTariff,
                energySharePct: numberOrBlank(e.target.value),
              },
            })
          }
          onBlur={() => markTouched("energyShare")}
          error={showError("energyShare", retailTariff.energySharePct)}
          helperText={
            showError("energyShare", retailTariff.energySharePct)
              ? validation.energyShare
              : ENERGY_SHARE_HELPER_TEXT
          }
          inputProps={{ step: "any", min: 0, max: 100 }}
        />
        )}
        {(retailSplit.shareSet || retailSplit.dynamic) && (
          <TextField
            fullWidth
            sx={{ mt: 2 }}
            label={REGULATED_ESCALATION_LABEL}
            type="number"
            value={retailTariff.regulatedEscalationPctYear ?? ""}
            placeholder={retailSplit.inflationPct}
            disabled={!retailSplit.active}
            InputLabelProps={{ shrink: true }}
            onChange={(e) =>
              patch({
                retailTariff: {
                  ...retailTariff,
                  regulatedEscalationPctYear: numberOrBlank(e.target.value),
                },
              })
            }
            onBlur={() => markTouched("regulatedEscalation")}
            error={showError("regulatedEscalation", retailTariff.regulatedEscalationPctYear)}
            helperText={
              showError("regulatedEscalation", retailTariff.regulatedEscalationPctYear) ? (
                validation.regulatedEscalation
              ) : (
                <>
                  <span>{REGULATED_ESCALATION_HELPER_TEXT}</span>{" "}
                  <span>{retailSplit.inflationPct || "0"}</span> <span>%/year</span>
                </>
              )
            }
            inputProps={{ step: "any" }}
          />
        )}
        {(retailSplit.shareSet || retailSplit.dynamic) && (
          <Alert severity={retailSplit.active ? "info" : "warning"} sx={{ mt: 1.5 }}>
            {retailSplit.dynamic
              ? RETAIL_DYNAMIC_ON_TEXT
              : retailSplit.active
                ? RETAIL_SPLIT_ON_TEXT
                : RETAIL_SPLIT_NO_PATH_TEXT}
          </Alert>
        )}
      </Box>

      {warnings.length > 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {warnings.map((warning, idx) => (
            <Typography key={idx} variant="body2">
              {warning}
            </Typography>
          ))}
        </Alert>
      )}

      <FormHelperText sx={{ mt: 1.5 }}>
        No wholesale market, capacity or balancing revenue is modelled for a
        behind-the-meter project — savings are settled against the retail tariff
        only. Intraday and balancing products are rejected for this revenue
        model.
      </FormHelperText>
    </Box>
  );
}
